/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState } from 'react';
import { Select, Modal, Typography } from '@crisellevaldez/speculo-ui';
import { useApolloClient } from '@apollo/client/react';
import { EmptyStateWithIcon } from '../../../common/EmptyStateWithIcon';
import { LayoutGrid } from 'lucide-react';
import { TablePagination } from '../../../common/TablePagination';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import { handleDateRangeChange as convertDateRange } from '../../../../utils/dateRangeTimezone';
import { CallDetailsPanel } from '../../contacts/CallDetailsPanel';
import { ActivityTableHeader } from './ActivityTableHeader';
import { ActivityGroup } from './ActivityGroup';
import { ActivityLoadingState } from './ActivityLoadingState';
import { useProcessActivities } from './ActivityProcessor';
import { getFlowInfo } from '../../../../utils/activity/activityHelpers';
import { useContactActivityFeedGQL } from '../../../../hooks/graphql/contacts/useContactActivityFeedGQL';
import { isPreviewContactId, PREVIEW_ACTIVITY_FEED } from '../../../../pages/previewMockData';
import { GET_ORG_CALL_BY_ID } from '../../../../lib/graphql/queries/organizations';
// import { NextFollowUpCard } from './NextFollowUpCard';
import { FutureOutreachPlanCard } from './FutureOutreachPlanCard';
import { EditCallAnalysisModal } from '../../contacts/EditCallAnalysisModal';
import { EmailPreviewModal } from './EmailPreviewModal';
import { SmsThreadModal, type SmsThreadAnchor } from '../../dashboard/SmsThreadModal';
import { EmailThreadModal } from '../../dashboard/EmailThreadModal';
import { useFlywheelEnabled } from '../../../../stores/useOrganizationStore';
import type { Contact } from '../../../../types/contact.types';
import type { GQLMessageItem } from '../../../../hooks/graphql/contacts/useContact';

/** Convert an activity feed call item (with metadata JSON) to CallDetailsPanel shape */
function adaptActivityCallForPanel(item: any) {
  let meta: any = {};
  try {
    meta = item.metadata ? JSON.parse(item.metadata) : {};
  } catch { /* ignore parse errors */ }

  const answers = (() => {
    try { return meta.call_answers ? JSON.parse(meta.call_answers) : undefined; }
    catch { return undefined; }
  })();
  const timeline = (() => {
    try {
      const parsed = meta.call_timeline ? JSON.parse(meta.call_timeline) : undefined;
      return Array.isArray(parsed) ? parsed : undefined;
    } catch { return undefined; }
  })();

  const isInbound = (meta.direction === 'inbound') || (item.channel === 'inbound');

  return {
    id: item.callId || item.eventId,
    call_id: item.callId || item.eventId,
    inbound: isInbound,
    from_number: meta.from_number || null,
    to_number: meta.to_number || null,
    callTimestamp: item.eventAt ? new Date(item.eventAt) : null,
    call_length: item.durationSeconds != null ? item.durationSeconds / 60 : null,
    duration: item.durationSeconds ?? 0,
    answered_by: meta.answered_by ?? null,
    call_analysis: {
      outcome: item.outcome || null,
      summary: item.summary || null,
      timeline,
      answers,
    },
    concatenated_transcript: meta.transcript ?? undefined,
    recordingUrl: meta.recording_url ?? undefined,
    sentiment: item.sentiment,
    isTest: meta.is_test === 'true',
    flowExecution: null,
  };
}

/** Convert a CallSummary from organization.calls(callId) into CallDetailsPanel shape */
function adaptOrgCallForPanel(c: any) {
  const timeline = (() => {
    try {
      const parsed = c.callAnalysisTimeline ? JSON.parse(c.callAnalysisTimeline) : undefined;
      return Array.isArray(parsed) ? parsed : undefined;
    } catch { return undefined; }
  })();
  const answers = (() => {
    try { return c.callAnalysisAnswers ? JSON.parse(c.callAnalysisAnswers) : undefined; }
    catch { return undefined; }
  })();

  return {
    id: c.callId,
    call_id: c.callId,
    inbound: c.inbound ?? false,
    from_number: c.fromNumber ?? null,
    to_number: c.toNumber ?? null,
    callTimestamp: c.initiatedAt ? new Date(c.initiatedAt) : null,
    call_length: c.durationSeconds != null ? c.durationSeconds / 60 : null,
    duration: c.durationSeconds ?? 0,
    corrected_duration: c.durationSeconds ?? 0,
    answered_by: c.answeredBy ?? null,
    call_analysis: {
      outcome: c.outcome ?? null,
      summary: c.summary ?? null,
      timeline,
      answers,
    },
    concatenated_transcript: c.transcript ?? undefined,
    recordingUrl: c.recordingUrl ?? undefined,
    sentiment: c.sentiment,
    isTest: c.isTest ?? false,
    contactId: c.contactId ?? undefined,
    assignedAgentId: c.assignedAgentId ?? null,
    assignedAgent: c.assignedAgent ?? null,
    flowExecution: (c.flowName || c.flowType)
      ? { id: '', call_id: c.callId, flow_name: c.flowName ?? '', flow_type: c.flowType ?? '' }
      : null,
  };
}

// Event type filter options — values match activityFeed eventTypes arg
const eventTypeOptions = [
  { value: 'email', label: 'Email' },
  { value: 'call', label: 'Calls' },
  { value: 'sms', label: 'SMS' },
  { value: 'website', label: 'Website' },
  { value: 'note', label: 'Notes' },
];

// event_type values the UI renders. Pushed to the server so the result set
// matches what we render and pagination doesn't loop through hidden rows.
// Signups surface through the site's lead_capture event, so the WebUsers
// 'registration' row is left out to avoid showing one signup twice.
const VISIBLE_EVENT_TYPES = [
  'call', 'email', 'sms', 'note', 'crm_note',
  'property_view', 'search', 'lead_capture', 'property_saved', 'valuation_request',
];

// 'website' in the dropdown is a UI bucket — expand it to the actual
// event_type values stored in WebsiteActivity.
const VISIBLE_WEB_EVENT_TYPES = [
  'property_view', 'search', 'lead_capture', 'property_saved', 'valuation_request',
];

const expandEventTypeFilters = (filters: string[]): string[] => {
  if (filters.length === 0) return VISIBLE_EVENT_TYPES;
  const out = new Set<string>();
  for (const f of filters) {
    if (f === 'website') VISIBLE_WEB_EVENT_TYPES.forEach((t) => out.add(t));
    else if (f === 'note') { out.add('note'); out.add('crm_note'); }
    else out.add(f);
  }
  return Array.from(out);
};

interface ActivityTabProps {
  contactId: string;
  firebaseUid?: string;
  contactFirstName?: string;
  contact?: Contact | null;
  initialDateRange?: { from: Date | null; to: Date | null };
  handoffNotes?: Array<{
    addedBy: string;
    addedByName: string;
    note: string;
    timestamp: string | { seconds: number; nanoseconds: number };
  }>;
}

export const ActivityTab: React.FC<ActivityTabProps> = ({
  contactId,
  contactFirstName,
  contact,
  handoffNotes,
}) => {
  const flywheelEnabled = useFlywheelEnabled();
  const { organization } = useOrganizationStore();
  const apolloClient = useApolloClient();

  const [selectedCall, setSelectedCall] = useState<any>(null);
  const [selectedEmail, setSelectedEmail] = useState<GQLMessageItem | null>(null);
  // SMS row click — opens a focused chat view scoped to this contact.
  // `anchor` carries the clicked row's data so the modal can render it
  // locally between the older/newer cursor buckets and scroll to it.
  const [smsThread, setSmsThread] = useState<{
    anchor: SmsThreadAnchor | null;
    totalCount: number | null;
  } | null>(null);
  // Agentic email row click — opens the conversation at that message.
  const [emailThread, setEmailThread] = useState<{
    messageId: string;
    conversationId: string | null;
    totalCount: number | null;
  } | null>(null);
  const [editOutcomeCallId, setEditOutcomeCallId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{ from: Date | null; to: Date | null }>({
    from: null,
    to: null,
  });
  const [eventTypeFilters, setEventTypeFilters] = useState<string[]>([]);

  const handleFilterChange = (value: string | string[]) => {
    setEventTypeFilters(Array.isArray(value) ? value : [value]);
    resetPage();
  };

  // SMS is a flywheel-only channel — non-flywheel orgs don't see the SMS
  // filter chip or SMS rows in the contact activity feed.
  const filterOptions = flywheelEnabled
    ? eventTypeOptions
    : eventTypeOptions.filter((o) => o.value !== 'sms');

  const effectiveEventTypes = useMemo(() => {
    const types = expandEventTypeFilters(eventTypeFilters);
    return flywheelEnabled ? types : types.filter((t) => t !== 'sms');
  }, [eventTypeFilters, flywheelEnabled]);

  const isPreview = isPreviewContactId(contactId);
  const live = useContactActivityFeedGQL(
    isPreview ? '' : contactId,
    dateRange,
    effectiveEventTypes,
  );
  const items: any[] = isPreview ? PREVIEW_ACTIVITY_FEED : live.items;
  const total = isPreview ? PREVIEW_ACTIVITY_FEED.length : live.total;
  const hasNext = isPreview ? false : live.hasNext;
  const loading = isPreview ? false : live.loading;
  const handleNextPage = live.handleNextPage;
  const resetPage = live.resetPage;

  const handleDateRangeChange = (range: { from: Date | null; to: Date | null }) => {
    const { dateRange: converted } = convertDateRange(range, organization?.timezone);
    setDateRange(converted);
    resetPage();
  };

  const { groupedActivities } = useProcessActivities({
    emailsData: undefined,
    emailCampaignsData: undefined,
    callsData: undefined,
    flowExecutionsData: undefined,
    webActivityData: undefined,
    handoffNotes,
    activityFeedData: items.length ? { items } : undefined,
    eventTypeFilters: [],
    dateRange,
    currentPage: 0,
    itemsPerPage: items.length || 1,
  });

  return (
    <div className="flex flex-col h-full min-h-0 gap-4 -m-3 md:-m-4 lg:-m-5 p-3 md:p-4 lg:p-5 bg-white dark:bg-card">
      {flywheelEnabled && contact?.flywheelOptOut === false && (
        <div className="remi-card-gradient px-4 md:px-5 py-4 flex-shrink-0 max-h-[320px] overflow-y-auto scrollbar-custom pr-1">
          <FutureOutreachPlanCard contact={contact} />
        </div>
      )}

      {/* NextFollowUpCard hidden for now while the Future outreach plan
          preview is the headline. Re-enable once the backend feeds both. */}
      {/* <NextFollowUpCard contactId={contactId} /> */}

      <ActivityTableHeader
        title="Communication so far"
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        count={total}
        isLoading={loading}
      >
        <div className="w-[150px]">
          <Select
            placeholder="Filter activity"
            options={filterOptions}
            value={eventTypeFilters}
            onChange={handleFilterChange}
            multiple={true}
          />
        </div>
      </ActivityTableHeader>

      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-custom space-y-4 pr-1">
      {loading && items.length === 0 ? (
        <ActivityLoadingState />
      ) : groupedActivities.length === 0 ? (
        <>
          <EmptyStateWithIcon
            message={hasNext ? 'No visible activity on this page. Continue to the next page.' : 'No activity found. Try adjusting your filters or check back later.'}
            icon={<LayoutGrid className="h-6 w-6 lg:h-8 lg:w-8 text-muted-foreground" />}
          />
          {hasNext && <TablePagination hasNext={hasNext} onNextPage={handleNextPage} isLoading={loading} />}
        </>
      ) : (
        <>
          {groupedActivities.map(([dateGroup, groupActivities]) => (
            <ActivityGroup
              key={dateGroup}
              dateGroup={dateGroup}
              activities={groupActivities}
              newlyAddedItems={[]}
              onSelectEmail={(emailData: any) => {
                const threadMessageId: string = emailData.messageId || emailData.eventId || '';
                if (
                  emailData.emailKind === 'AGENTIC' &&
                  threadMessageId &&
                  !threadMessageId.startsWith('el:')
                ) {
                  setEmailThread({
                    messageId: threadMessageId,
                    conversationId: emailData.conversationId || null,
                    totalCount: emailData.threadMessageCount ?? null,
                  });
                  return;
                }
                // The activity feed's email shape is partial — patch it
                // up to GQLMessageItem so EmailPreviewModal renders the
                // full From / To / Subject / body layout. Missing fields
                // fall back to empty strings so the modal still works.
                // Use the activity feed's eventAt as the canonical time so
                // the modal headline matches what the list row shows.
                // Other timestamp fields (sentAt / deliveredAt / openedAt)
                // are template metadata times — they can drift from when
                // the event was actually surfaced on this contact's feed.
                const eventTime = emailData.eventAt || emailData.createdAt || '';
                const adapted: GQLMessageItem = {
                  messageId: emailData.messageId || emailData.eventId || emailData.id || '',
                  conversationId: emailData.conversationId || '',
                  channel: emailData.channel || 'email',
                  direction: emailData.direction || 'outbound',
                  fromAddress: emailData.fromAddress || '',
                  toAddress: emailData.toAddress || contact?.email || '',
                  subject: emailData.subject || emailData.title || '',
                  body: emailData.body || '',
                  bodyHtml: emailData.bodyHtml ?? null,
                  status: emailData.status || emailData.outcome || '',
                  eventType: emailData.eventType ?? null,
                  templateName: emailData.templateName ?? null,
                  campaignId: emailData.campaignId ?? null,
                  campaignStatus: emailData.campaignStatus ?? null,
                  scheduledTime: emailData.scheduledTime ?? null,
                  deliveredAt: emailData.deliveredAt ?? null,
                  openedAt: emailData.openedAt ?? null,
                  createdAt: eventTime,
                  sentAt: eventTime,
                };
                setSelectedEmail(adapted);
              }}
              onSelectSms={(smsData: any) => {
                // Build the anchor from the clicked activity row.
                // Backend's before/after cursors are exclusive of the
                // anchor (PR #2423), so the modal renders this bubble
                // locally between the older/newer buckets.
                let meta: any = {};
                try {
                  meta = smsData?.metadata ? JSON.parse(smsData.metadata) : {};
                } catch {
                  /* ignore */
                }
                const messageId: string | undefined = smsData?.messageId || smsData?.eventId;
                const anchor: SmsThreadAnchor | null = messageId
                  ? {
                      messageId,
                      body: meta?.body ?? smsData?.summary ?? null,
                      direction: meta?.direction ?? smsData?.direction ?? null,
                      sentAt: meta?.sent_at ?? meta?.sentAt ?? smsData?.eventAt ?? null,
                      createdAt: smsData?.createdAt ?? smsData?.eventAt ?? null,
                    }
                  : null;
                setSmsThread({ anchor, totalCount: smsData?.threadMessageCount ?? null });
              }}
              onSelectCall={async (callData: any) => {
                // Activity feed entries: re-fetch the full call by callId so
                // the details panel renders fresh data (not stale metadata
                // from the feed). Show the panel immediately with the adapted
                // metadata so it isn't blank, then swap to the canonical
                // call payload once the lookup resolves.
                if (callData?.eventType === 'call' && callData?.metadata) {
                  setSelectedCall(adaptActivityCallForPanel(callData));
                  const callId = callData.callId || callData.eventId;
                  if (callId && organization?.id) {
                    try {
                      const { data } = await apolloClient.query<{
                        organization?: { calls?: { items?: any[] } } | null;
                      }>({
                        query: GET_ORG_CALL_BY_ID,
                        variables: { organizationId: organization.id, callId },
                        fetchPolicy: 'cache-first',
                      });
                      const fresh = data?.organization?.calls?.items?.[0];
                      if (fresh) {
                        setSelectedCall(adaptOrgCallForPanel(fresh));
                      }
                    } catch (err) {
                      console.warn('[ActivityTab] failed to fetch call by id', err);
                    }
                  }
                } else {
                  setSelectedCall(callData);
                }
              }}
              timezone={organization?.timezone}
            />
          ))}

          <TablePagination
            hasNext={hasNext}
            onNextPage={handleNextPage}
            isLoading={loading}
          />
        </>
      )}
      </div>
      {/* Call details modal — matches the Calls tab pattern (no more
          full-tab takeover). */}
      <Modal
        isOpen={!!selectedCall}
        onClose={() => setSelectedCall(null)}
        size="xl"
      >
        <Modal.Header>
          <Typography variant="h3">Call details</Typography>
        </Modal.Header>
        <Modal.Body className="overflow-hidden">
          <div className="max-h-[calc(100vh-200px)] overflow-y-auto scrollbar-custom">
            {selectedCall && (
              <CallDetailsPanel
                call={selectedCall}
                orgTimezone={organization?.timezone}
                flowInfo={getFlowInfo(selectedCall)}
                contactFirstName={contactFirstName || ''}
                assistantName={organization?.assistant_name}
                onEditOutcome={() =>
                  setEditOutcomeCallId(selectedCall?.call_id || selectedCall?.id)
                }
              />
            )}
          </div>
        </Modal.Body>
      </Modal>

      <EmailPreviewModal
        isOpen={!!selectedEmail}
        onClose={() => setSelectedEmail(null)}
        email={selectedEmail}
        contactName={
          [contact?.firstName, contact?.lastName].filter(Boolean).join(' ').trim() ||
          contactFirstName ||
          undefined
        }
        contactEmail={contact?.email || undefined}
      />

      <EmailThreadModal
        isOpen={!!emailThread}
        onClose={() => setEmailThread(null)}
        contactId={contactId}
        contactName={
          [contact?.firstName, contact?.lastName].filter(Boolean).join(' ').trim() ||
          contactFirstName ||
          null
        }
        contactEmail={contact?.email || null}
        conversationId={emailThread?.conversationId ?? null}
        anchorMessageId={emailThread?.messageId ?? null}
        totalCount={emailThread?.totalCount ?? null}
      />

      <SmsThreadModal
        isOpen={!!smsThread}
        onClose={() => setSmsThread(null)}
        contactId={contactId}
        contactName={
          [contact?.firstName, contact?.lastName].filter(Boolean).join(' ').trim() ||
          contactFirstName ||
          null
        }
        contactPhone={contact?.phoneNumber || null}
        anchor={smsThread?.anchor ?? null}
        totalCount={smsThread?.totalCount ?? null}
      />

      {editOutcomeCallId && (
        <EditCallAnalysisModal
          isOpen={!!editOutcomeCallId}
          onClose={() => setEditOutcomeCallId(null)}
          callId={editOutcomeCallId}
          currentOutcome={selectedCall?.call_analysis?.outcome || ''}
          onSuccess={(newOutcome) => {
            if (selectedCall) {
              setSelectedCall({
                ...selectedCall,
                call_analysis: { ...selectedCall.call_analysis, outcome: newOutcome },
              });
            }
          }}
        />
      )}
    </div>
  );
};
